/**
 * Page show the details of the user'account.
 *
 * Copyright © Ircam Amplify 2024-2025
 */
import { fetchAuthSession } from 'aws-amplify/auth'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { API, fetchUser } from '../api'
import { User, getUserState, useSelector } from '../store'
import { IampLogger } from '../utils/IampLogger'
import FormReturnHandler from './FormReturnHandler'

export const handleUpdatePayment = async (
  user?: User | null,
  handleStripeError: (msg: string) => void = () => {},
  setStripeRequestProcessing: (value: boolean) => void = () => {}
) => {
  try {
    if (user?.user_stripe_id === null || user?.user_stripe_id === undefined) {
      await fetchUser()
      handleStripeError?.(
        'No Stripe payment information found, please retry in a few seconds'
      )
      return
    }
    setStripeRequestProcessing?.(true)
    const res = await API(await fetchAuthSession()).post(
      '/stripe/create-edit-payment-session',
      { user_stripe_id: user?.user_stripe_id }
    )
    setStripeRequestProcessing?.(false)
    if (res.data.error) {
      handleStripeError(res.data.error)
      return
    }
    // nagivate to stripe portal
    window.location.href = res.data.session.url
  } catch (e) {
    IampLogger.error(e)
    handleStripeError(
      'Error while launching the Stripe edit informations portal'
    )
    setStripeRequestProcessing(false)
  }
}

export default function MyAccount() {
  const { user } = useSelector(getUserState)

  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  useEffect(() => {
    fetchUser()
  }, [])

  const updateUserProfile = async (values: any) => {
    try {
      await API(await fetchAuthSession()).patch('/user', values)
      await fetchUser()
      setSuccessMessage('Profile updated successfully')
    } catch (e) {
      setErrorMessage('Error saving your profile')
    }
  }

  return (
    <>
      {errorMessage && (
        <FormReturnHandler
          open={!!errorMessage}
          success={false}
          message={errorMessage}
          onClose={() => setErrorMessage('')}
        />
      )}
      {successMessage && (
        <FormReturnHandler
          open={!!successMessage}
          success
          message={successMessage}
          onClose={() => setSuccessMessage('')}
        />
      )}
      <div className="max-w-[688px]">
        <h1 className="text-primary-900 font-medium text-[30px] mb-5 leading-[40px]	">
          My Account
        </h1>
        {user && (
          <div className="w-full mb-5">
            <Formik
              onSubmit={updateUserProfile}
              initialValues={{
                first_name: user.user_firstname,
                last_name: user.user_lastname,
                role: user.role,
              }}
            >
              {({ isSubmitting }) => (
                <Form>
                  <div className="flex items-center justify-center my-4">
                    <div className="grid grid-cols-2 gap-4 w-full">
                      <div>
                        <label htmlFor="first_name" className="text-sm">
                          First name
                          <Field
                            type="text"
                            id="first_name"
                            name="first_name"
                            className="ircam-input w-full"
                          />
                          <ErrorMessage
                            name="first_name"
                            className="text-ircamprimary-accent"
                            component="span"
                          />
                        </label>
                      </div>
                      <div>
                        <label htmlFor="last_name" className="text-sm">
                          Last name
                          <Field
                            type="text"
                            id="last_name"
                            name="last_name"
                            className="ircam-input w-full"
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="w-full my-4">
                    <label htmlFor="user_email" className="text-sm">
                      Email address
                      <Field
                        type="text"
                        id="user_email"
                        name="user_email"
                        className="
                          ircam-input
                          w-full bg-gray-500
                          cursor-not-allowed
                        "
                        value={user.user_email}
                        readOnly
                      />
                    </label>
                  </div>

                  <div className="w-full my-4">
                    <label htmlFor="role" className="text-sm">
                      Role
                      <Field
                        as="select"
                        id="role"
                        name="role"
                        className="ircam-input w-full"
                      >
                        <option value="placeholder">Select your role</option>
                        <option value="developer">Developer</option>
                        <option value="techlead">
                          Tech/Engineering team lead
                        </option>
                        <option value="cto">CIO / CTO / IT manager</option>
                        <option value="csuite">CEO/management</option>
                        <option value="business">
                          Product manager / marketing / sales
                        </option>
                      </Field>
                    </label>
                  </div>

                  <div className="w-full my-4">
                    <label htmlFor="password" className="text-sm w-full">
                      Password
                      <div
                        className="
                        ircam-input w-full h-10
                        flex items-center justify-between
                      "
                      >
                        <span className="text-xl ml-2">●●●●●●●●</span>
                        <Link
                          to="/change-password"
                          className="
                            mr-2 text-ircamprimary-accent
                            hover:underline
                          "
                        >
                          Edit
                        </Link>
                      </div>
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="my-4">
                      <button
                        type="submit"
                        className="
                          w-2/6 self-center
                          button button-large button-dark
                          py-2 px-4
                        "
                        disabled={isSubmitting}
                      >
                        <div className="button button-label">Save</div>
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}
      </div>
    </>
  )
}
